import {
  chungTuSearchColumns,
  donViSearchColumns,
  khachHangSearchColumns,
  quyenChungTuSearchColumns
} from '@/constants/search-columns';
import {
  RadixHoverDropdown,
  DateRangeDropdownV2 as DateRangeField,
  FormField,
  SearchField
} from '@/components/custom/erp';
import { ChungTu, DonViChiNhanh, KhachHang, QuyenChungTu } from '@/types/schemas';
import { CreateEditCustomerHeaderTab } from './create-edit-customer-tabs';
import { useSearchFieldStates } from '../../hooks/useSearchFieldStates';
import { Label } from '@/components/ui/label';
import { QUERY_KEYS } from '@/constants';

interface BasicInfoProps {
  formMode: 'add' | 'edit' | 'view';
  searchFieldStates: ReturnType<typeof useSearchFieldStates>;
}

export const BasicInfoTab = ({ formMode, searchFieldStates }: BasicInfoProps) => {
  return (
    <div className='max-h-[calc(100vh-150px)] overflow-y-auto'>
      <div className='p-4 md:p-6'>
        <div className='flex flex-col gap-y-4 md:gap-y-6'>
          <div className='space-y-4 md:space-y-6'>
            <DateRangeField fromDateName='ngay_ct1' toDateName='ngay_ct2' />
            {/* Document Type Field */}
            <div className='flex flex-col sm:flex-row sm:items-center'>
              <Label className='mb-2 sm:mb-0 sm:w-40 sm:min-w-40'>Loại chứng từ</Label>
              <SearchField<ChungTu>
                type='text'
                columnDisplay='ma_ct'
                displayRelatedField='ten_ct'
                searchEndpoint={`/${QUERY_KEYS.CHUNG_TU}`}
                searchColumns={chungTuSearchColumns}
                value={searchFieldStates.documentType?.ma_ct || ''}
                relatedFieldValue={searchFieldStates.documentType?.ten_ct || ''}
                onRowSelection={searchFieldStates.setDocumentType}
                disabled={formMode === 'view'}
                dialogTitle='Danh mục loại chứng từ'
              />
            </div>

            {/* Authorization Code Field */}
            <div className='flex flex-col sm:flex-row sm:items-center'>
              <Label className='mb-2 sm:mb-0 sm:w-40 sm:min-w-40'>Mã quyển</Label>
              <SearchField<QuyenChungTu>
                type='text'
                columnDisplay='ma_nk'
                displayRelatedField='ten_nk'
                searchEndpoint={`/${QUERY_KEYS.QUYEN_CHUNG_TU}`}
                searchColumns={quyenChungTuSearchColumns}
                value={searchFieldStates.authorizationCode?.ma_nk || ''}
                relatedFieldValue={searchFieldStates.authorizationCode?.ten_nk || ''}
                onRowSelection={searchFieldStates.setAuthorizationCode}
                disabled={formMode === 'view'}
                dialogTitle='Danh mục quyển chứng từ'
              />
            </div>

            {/* Document Number Field */}
            <div className='flex gap-2 sm:flex-row sm:items-center'>
              <div className='flex flex-col sm:flex-row sm:items-center'>
                <Label className='mb-2 sm:mb-0 sm:w-40 sm:min-w-40'>Số c/từ(từ/đến)</Label>
                <FormField type='text' name='so_ct1' disabled={formMode === 'view'} />
              </div>
              <FormField type='text' name='so_ct2' disabled={formMode === 'view'} />
            </div>

            {/* Customer Code Field */}
            <div className='flex flex-col sm:flex-row sm:items-center'>
              <Label className='mb-2 sm:mb-0 sm:w-40 sm:min-w-40'>Mã khách hàng</Label>
              <SearchField<KhachHang>
                type='text'
                columnDisplay='customer_code'
                displayRelatedField='customer_name'
                searchEndpoint={`/${QUERY_KEYS.KHACH_HANG}`}
                searchColumns={khachHangSearchColumns}
                value={searchFieldStates.customer?.customer_code || ''}
                relatedFieldValue={searchFieldStates.customer?.customer_name || ''}
                onRowSelection={searchFieldStates.setCustomer}
                disabled={formMode === 'view'}
                dialogTitle='Danh mục khách hàng'
                headerFields={<CreateEditCustomerHeaderTab />}
              />
            </div>

            {/* Unit Field */}
            <div className='flex flex-col sm:flex-row sm:items-center'>
              <Label className='mb-2 sm:mb-0 sm:w-40 sm:min-w-40'>Đơn vị</Label>
              <SearchField<DonViChiNhanh>
                type='text'
                columnDisplay='ma_unit'
                displayRelatedField='ten_unit'
                searchEndpoint={`/${QUERY_KEYS.DON_VI}`}
                searchColumns={donViSearchColumns}
                value={searchFieldStates.unit?.ma_unit || ''}
                relatedFieldValue={searchFieldStates.unit?.ten_unit || ''}
                onRowSelection={searchFieldStates.setUnit}
                disabled={formMode === 'view'}
                dialogTitle='Danh mục đơn vị'
              />
            </div>

            {/* Report Format Field */}

            <div className='flex flex-col gap-2 lg:flex-row lg:items-start'>
              <div className='flex flex-col sm:flex-row sm:items-center'>
                <Label htmlFor='reportTemplate' className='mb-2 sm:mb-0 sm:w-40 sm:min-w-40'>
                  Mẫu lọc báo cáo
                </Label>
                <div className='w-56'>
                  <FormField
                    id='reportTemplate'
                    type='select'
                    name='reportTemplate'
                    disabled={formMode === 'view'}
                    options={[{ label: 'Người dùng tự lọc', value: 0 }]}
                  />
                </div>
              </div>

              <div className='mt-2 h-8 w-8 self-end ring-0 hover:ring-0 sm:mt-0 sm:self-center lg:self-center'>
                <RadixHoverDropdown
                  buttonClassName='size-8'
                  iconNumber={624}
                  items={[
                    {
                      value: 'create_new',
                      label: 'Lưu mẫu mới',
                      icon: 7
                    },
                    {
                      value: 'edit',
                      label: 'Đè vào mẫu đang chọn',
                      icon: 75
                    },
                    {
                      value: 'delete',
                      label: 'Xóa mẫu đang chọn',
                      icon: 8,
                      onClick: () => console.log('Delete current filter template')
                    }
                  ]}
                />
              </div>
            </div>

            {/* Verification Type Field */}
            <div className='flex flex-col sm:flex-row sm:items-center'>
              <Label className='mb-2 sm:mb-0 sm:w-40 sm:min-w-40'>Loại kiểm tra</Label>
              <div className='w-56'>
                <FormField
                  type='select'
                  name='xtype'
                  disabled={formMode === 'view'}
                  options={[
                    { label: 'Danh sách chứng từ chờ phát hành', value: 'pending_issuance' },
                    { label: 'Danh sách chứng từ đã xác thực (xác thực, thay đổi, thay thế, hủy)', value: 'verified' },
                    { label: 'Danh sách chứng từ đã bị điều chỉnh', value: 'adjusted' },
                    { label: 'Danh sách chứng từ đã bị thay thế', value: 'replaced' },
                    {
                      label: 'Danh sách chứng từ của khách hàng sử dụng HĐĐT ở trạng thái [Không sử dụng]',
                      value: 'unused'
                    }
                  ]}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
